import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import CustomDialog from "../CustomDialog";
import NullCheck from "components/NullCheck";
import { sortArray } from "config/functions";

const SelectInput = ({
  control,
  title,
  name,
  required,
  errors,
  width,
  validation,
  options,
  disabled,
  defaultValue,
  onChanged,
  addFlex,
  searchable = true,
  searchPlaceholder = "SEARCH",
  filterKeys = ["name"],
}) => {
  const { bt } = useBizTypeTranslation();
  const [openMassge, setOpenMassge] = useState(false);
  const [search, setSearch] = useState("");

  const error = errors ? errors[name]?.ref?.name : false;
  const massge = errors ? errors[name]?.message : "";

  const sortedOptions = useMemo(
    () => sortArray(options ?? [], "name") ?? [],
    [options]
  );

  const filteredOptions = useMemo(() => {
    if (!searchable) return sortedOptions;

    const q = (search || "").trim().toLowerCase();
    if (!q) return sortedOptions;

    return sortedOptions.filter((op) => {
      if (!op) return false;

      return filterKeys.some((k) => {
        const v = op?.[k];
        return String(v ?? "").toLowerCase().includes(q);
      });
    });
  }, [sortedOptions, search, searchable, filterKeys]);

  const labelText = title ? `${required ? "*" : ""}${bt(title)}` : "";

  return (
    <Stack sx={{ flex: addFlex ? 1 : 0, minWidth: 0 }}>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue ?? ""}
        rules={{
          required: required ? bt("REQUIRED") : false,
          pattern: {
            value: validation?.regex,
            message: bt(validation?.massge),
          },
        }}
        render={({ field: { onChange, value } }) => (
          <FormControl
            size="small"
            variant="outlined"
            sx={{ minWidth: width ?? 200 }}
            error={error === name}
            disabled={disabled ?? false}
          >
            <InputLabel id={`${name}-label`}>{labelText}</InputLabel>

            <Select
              labelId={`${name}-label`}
              label={labelText}
              autoWidth
              sx={{ width: width ? width : 200, paddingX: 1 }}
              size="small"
              value={value}
              onClose={() => setSearch("")}
              MenuProps={{
                autoFocus: false,
                disableAutoFocusItem: true,
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    minWidth: width && width <= 200 ? width : 200,
                  },
                },
              }}
              onChange={(e) => {
                onChange(e);
                onChanged && onChanged((options ?? []).find((op) => op?.id === e.target.value));
              }}
            >
              <NullCheck valueForCheck={searchable}>
                <Stack px={1} py={0.5} bgcolor="background.paper" position="sticky" top={0} zIndex={1}>
                  <TextField
                    autoFocus
                    size="small"
                    fullWidth
                    variant="standard"
                    value={search}
                    placeholder={bt(searchPlaceholder)}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 28,
                        fontSize: 13,
                      },
                      "& .MuiInputBase-input": {
                        padding: "4px 8px",
                      },
                    }}
                  />
                </Stack>
              </NullCheck>


              {filteredOptions.length === 0 ? (
                <MenuItem disabled>
                  <Typography variant="body2">
                    {bt("NO_RESULTS")}
                  </Typography>
                </MenuItem>
              ) : (
                filteredOptions.map((option) => (
                  <MenuItem
                    key={option.id}
                    sx={{ minWidth: width ? width : 200 }}
                    value={option.id}
                  >
                    {bt(option.name)}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        )}
      />

      <NullCheck valueForCheck={massge}>
        <CustomDialog
          title="ERROR"
          open={openMassge}
          width={300}
          onClose={() => setOpenMassge(false)}
          closeBtn="CLOSE"
        >
          <Stack alignItems="center">{massge}</Stack>
        </CustomDialog>
      </NullCheck>
    </Stack>
  );
};

export default SelectInput;